import { z } from 'zod';
/**
 * Shared types for the Ask tool-calling layer (epic: function-calling
 * synthesis). Lives in @leanopx/shared so the server's tool registry +
 * the client's per-tool UI affordances (later: tool-call cards in the
 * Ask thread) agree on names + parameter shapes.
 *
 * Architecture:
 *   - Each Ask tool is a `name + description + parameters + execute`
 *     bundle (apps/functions/src/ask-tools/tools/*.ts).
 *   - Tools are registered via `registerAskTool()` at function init.
 *   - The Gemini synthesis loop calls `listAskTools()` to enumerate +
 *     translate to Vertex `FunctionDeclaration`s.
 *   - When the model emits a `FunctionCall`, the loop looks up the
 *     tool by name, parses args through its Zod schema, invokes
 *     `execute()`, and feeds the result back as a `FunctionResponse`.
 *
 * Tool result contract (the user's requirement that retrieved data
 * also updates our understanding):
 *   - `result`: trimmed, JSON-safe payload Gemini sees in the next
 *     turn. Keep concise — fits inside the model's context budget.
 *   - `enrichment`: optional `IngestBatch` of graph nodes/edges. On
 *     success, the orchestrator flushes this through the same
 *     `ingestBatch` pipeline that webhook + backfill use. First time
 *     we see "Tim's Q3 email" via Ask, it lands in the graph forever
 *     — cross-tool joins keep working because both sync + tool-call
 *     write to the same store.
 *   - `sources`: AskCite-compatible source list. The synthesis layer
 *     merges these into the final `[CITES]` block so the prose ↔ tool
 *     mapping is auditable.
 *   - `error`: typed failure shape. Tool execution errors don't crash
 *     the loop; they're returned to the model as part of the
 *     FunctionResponse so it can adapt.
 */
/**
 * The set of recognized Ask tools. Closed enum so client UI tooling
 * and server registry stay in sync; adding a new tool means adding
 * to this enum (one place) + registering an adapter that satisfies
 * the contract.
 */
export const ASK_TOOL_NAMES = [
    'queryLeanOpxGraph',
    'searchSlackMessages',
    'searchEmails',
    'lookupSalesforceContact',
    'lookupGithubContext',
    'getRecentInsights',
    'getRecentSignals',
];
export const AskToolName = z.enum(ASK_TOOL_NAMES);
/**
 * Typed failure shape returned in `AskToolResult.error`. Closed enum so
 * the synthesis layer can branch on cause cleanly (e.g. "vendor not
 * configured" → tell the model "I don't have a Slack token yet" rather
 * than retrying).
 */
/**
 * Tri-state severity surfaced by Ask tool results that read severity-
 * carrying entities (signals, alerts). The internal vocabulary varies
 * by domain (signals use info/watch/critical; alerts use info/warn/
 * page); tools normalize to this enum so the model sees one
 * vocabulary across all tool outputs.
 *
 * Distinct from `DecisionConfidence` (decisions/decision.ts) — that's
 * about confidence in a synthesized claim, this is about severity of
 * a raw event. Same string values, different semantics.
 */
export const ASK_TOOL_SEVERITY_TIERS = ['low', 'medium', 'high'];
export const AskToolSeverityTier = z.enum(ASK_TOOL_SEVERITY_TIERS);
export const ASK_TOOL_ERROR_CODES = [
    'vendor_not_configured',
    'invalid_arguments',
    'no_results',
    'rate_limited',
    'permission_denied',
    'vendor_error',
    'timeout',
    'unknown',
];
export const AskToolErrorCode = z.enum(ASK_TOOL_ERROR_CODES);
export const AskToolError = z.object({
    code: AskToolErrorCode,
    message: z.string().min(1).max(500),
});
/**
 * One sync-log-style summary row per tool invocation. Lands in the
 * `askToolInvocations` Firestore collection (TTL-bounded so the
 * collection stays small without app-side pruning) so the admin UI
 * can render "Ask ran 47 tool calls in the last hour".
 */
export const AskToolInvocation = z.object({
    /** Composite id: `<orgId>_<toolName>_<at-ms>`. */
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** The Ask thread / question this invocation belongs to. */
    threadId: z.string().optional(),
    toolName: AskToolName,
    actorUid: z.string().min(1),
    /** Arguments Gemini supplied (trimmed for storage; keep <=2KB). */
    args: z.record(z.string(), z.unknown()),
    /** Outcome of the invocation. */
    outcome: z.enum(['success', 'error', 'no_results']),
    /** Latency in ms (wall clock from execute() entry to return). */
    durationMs: z.number().int().nonnegative(),
    /** Number of items returned (messages found, rows returned, etc.). */
    itemsReturned: z.number().int().nonnegative(),
    /** Number of graph nodes+edges produced by the enrichment, if any. */
    enrichmentRecords: z.number().int().nonnegative(),
    /** Error details when outcome=error. */
    error: AskToolError.optional(),
    /** ISO timestamp. */
    at: z.string().min(1),
    /** Firestore TTL field for auto-expiry. */
    ttlAt: z.unknown(),
});
export const ASK_TOOL_INVOCATIONS_COLLECTION = 'askToolInvocations';
